import { errorInfoSync } from '@antreja/ui/errors';
import type { Person, PersonAppStatusRow } from './types';
import { errorText } from './errors';
import { fmtAgo, fmtDate } from './format';

/** App-status van een persoon: geen app, uitgenodigd, uitnodiging verlopen of actief (gekoppeld account). */
export type AppStatusKind = 'none' | 'invited' | 'expired' | 'active';

export interface AppStatus {
  kind: AppStatusKind;
  /** Korte tekst voor badge/kolom, bijv. "Uitgenodigd op 1 nov. 2026". */
  label: string;
  tone: 'neutral' | 'green' | 'yellow' | 'red';
  /** Open uitnodiging (ook als die verlopen is) — nodig voor intrekken. */
  inviteId: string | null;
  inviteEmail: string | null;
}

/**
 * Leidt de app-status af uit de persoon en (optioneel) de rij uit person_app_status.
 * Zonder rij (bijv. RPC nog niet geladen) valt hij terug op persons.profile_id.
 */
export function appStatus(person: Pick<Person, 'profile_id'>, row: PersonAppStatusRow | undefined, now = new Date()): AppStatus {
  const profileId = row?.profile_id ?? person.profile_id;
  if (profileId) {
    const last = row?.last_sign_in_at ?? null;
    return {
      kind: 'active',
      label: last ? `Actief (laatst ingelogd ${fmtAgo(last, now)})` : 'Actief',
      tone: 'green',
      inviteId: null,
      inviteEmail: null,
    };
  }
  if (row?.invite_id && row.invited_at) {
    const expired = !!row.invite_expires_at && new Date(row.invite_expires_at).getTime() <= now.getTime();
    return expired
      ? { kind: 'expired', label: 'Uitnodiging verlopen', tone: 'red', inviteId: row.invite_id, inviteEmail: row.invite_email }
      : {
          kind: 'invited',
          label: `Uitgenodigd op ${fmtDate(row.invited_at)}`,
          tone: 'yellow',
          inviteId: row.invite_id,
          inviteEmail: row.invite_email,
        };
  }
  return { kind: 'none', label: 'Geen app', tone: 'neutral', inviteId: null, inviteEmail: null };
}

/** Mag deze persoon (nog) een uitnodiging krijgen in een bulkactie? Alleen zonder app en zonder lopende uitnodiging. */
export function needsInvite(s: AppStatus): boolean {
  return s.kind === 'none' || s.kind === 'expired';
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface BulkPlan<P> {
  /** Krijgen een uitnodiging. */
  invite: P[];
  /** Geen (geldig) e-mailadres. */
  noEmail: P[];
  /** Minderjarig zonder bevestiging ouder/verzorger. */
  minorsSkipped: P[];
}

/** Verdeelt personen zonder app voor "Leden zonder app uitnodigen". */
export function planBulkInvite<P extends Pick<Person, 'email' | 'is_minor'>>(persons: P[], guardianInformed: boolean): BulkPlan<P> {
  const plan: BulkPlan<P> = { invite: [], noEmail: [], minorsSkipped: [] };
  for (const p of persons) {
    if (!p.email || !EMAIL_RE.test(p.email.trim())) plan.noEmail.push(p);
    else if (p.is_minor && !guardianInformed) plan.minorsSkipped.push(p);
    else plan.invite.push(p);
  }
  return plan;
}

/** Fouttekst voor uitnodigen: herkent de Nederlandse meldingen uit create_person_invite / invite-person. */
export function inviteErrorText(err: unknown): string {
  const i = errorInfoSync(err);
  const raw = `${i.bodyError ?? ''} ${i.message}`.toLowerCase();
  if (raw.includes('te veel uitnodigingen')) return 'Er zijn het afgelopen uur al 50 uitnodigingen verstuurd. Probeer het over een uur opnieuw.';
  if (raw.includes('ouder/verzorger')) return 'Bevestig eerst dat de ouder of verzorger is geïnformeerd.';
  if (raw.includes('ongeldig e-mailadres')) return 'Dit e-mailadres klopt niet.';
  if (raw.includes('andere persoon in deze club')) return 'Dit e-mailadres hoort al bij een andere persoon in deze club.';
  if (raw.includes('heeft de app al')) return 'Deze persoon heeft de app al.';
  if (raw.includes('versturen mislukt')) return 'De uitnodiging kon niet worden verstuurd. Probeer het later opnieuw.';
  return errorText(err, 'Uitnodigen is niet gelukt.');
}
